/**
 * Coordinates every public-web auth lane without retaining any credential
 * material. Transition permits order session-replacing actions (sign-in,
 * sign-up, verification links, and logout). Credential and state permits bind
 * background work to the exact session snapshot that started it.
 */
export function createAuthSessionMutationFence() {
  let transitionGeneration = 0;
  let credentialEpoch = 0;
  let stateEpoch = 0;
  let credentialIdentity = "";

  const normalizeIdentity = (identity) => String(identity || "");

  return {
    beginTransition(label) {
      transitionGeneration += 1;
      return Object.freeze({
        kind: "transition",
        generation: transitionGeneration,
        label: String(label || "auth"),
      });
    },

    isTransitionCurrent(permit) {
      return Boolean(
        permit && permit.kind === "transition" && permit.generation === transitionGeneration,
      );
    },

    retireTransition(permit) {
      if (!this.isTransitionCurrent(permit)) return false;
      transitionGeneration += 1;
      return true;
    },

    cancelAllTransitions() {
      transitionGeneration += 1;
    },

    synchronizeCredentials(identity) {
      const nextIdentity = normalizeIdentity(identity);
      if (nextIdentity === credentialIdentity) return false;
      credentialIdentity = nextIdentity;
      credentialEpoch += 1;
      stateEpoch += 1;
      return true;
    },

    noteCredentialsChanged(identity) {
      credentialIdentity = normalizeIdentity(identity);
      credentialEpoch += 1;
      stateEpoch += 1;
    },

    noteStateChanged() {
      stateEpoch += 1;
    },

    captureCredential() {
      return Object.freeze({
        kind: "credential",
        credentialEpoch,
        identity: credentialIdentity,
      });
    },

    captureState() {
      return Object.freeze({
        kind: "state",
        credentialEpoch,
        stateEpoch,
        identity: credentialIdentity,
      });
    },

    isCredentialCurrent(permit) {
      return Boolean(
        permit &&
        permit.kind === "credential" &&
        permit.credentialEpoch === credentialEpoch &&
        permit.identity === credentialIdentity,
      );
    },

    isStateCurrent(permit) {
      return Boolean(
        permit &&
        permit.kind === "state" &&
        permit.credentialEpoch === credentialEpoch &&
        permit.stateEpoch === stateEpoch &&
        permit.identity === credentialIdentity,
      );
    },
  };
}

/**
 * Gives a mounted auth surface explicit ownership of one transition permit.
 * A disposed surface cannot start or publish another transition, including
 * from a late browser-provider callback retained after navigation.
 */
export function createAuthTransitionOwner() {
  let activePermit = null;
  let mounted = true;

  return {
    activate() {
      mounted = true;
    },

    isMounted() {
      return mounted;
    },

    start(label, beginTransition) {
      if (!mounted) return null;
      const permit = beginTransition(label);
      activePermit = permit;
      return permit;
    },

    isCurrent(permit, isTransitionCurrent) {
      return Boolean(mounted && permit && activePermit === permit && isTransitionCurrent(permit));
    },

    retire(permit, cancelTransition) {
      if (!permit || activePermit !== permit) return false;
      activePermit = null;
      return cancelTransition(permit);
    },

    dispose(cancelTransition) {
      mounted = false;
      const permit = activePermit;
      activePermit = null;
      if (permit) cancelTransition(permit);
    },
  };
}

/**
 * Coalesces simultaneous refreshes for the same credential identity. A
 * rotation for another identity is intentionally a separate operation.
 */
export function createSingleFlightByKey() {
  const inFlight = new Map();

  return {
    run(key, task) {
      const normalizedKey = String(key);
      const existing = inFlight.get(normalizedKey);
      if (existing) return existing;

      const promise = Promise.resolve().then(task);
      inFlight.set(normalizedKey, promise);
      const cleanup = () => {
        if (inFlight.get(normalizedKey) === promise) {
          inFlight.delete(normalizedKey);
        }
      };
      void promise.then(cleanup, cleanup);
      return promise;
    },
  };
}
